import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Card } from '../primitives/card';
import { Skeleton } from '../primitives/skeleton';
import { cn } from '../lib/cn';

interface StatCardProps {
  label: string;
  value: ReactNode;
  icon?: LucideIcon;
  /** One short line under the value, e.g. "3 new today" */
  hint?: ReactNode;
  /** Highlights the card, e.g. for work waiting on the user */
  emphasis?: boolean;
  loading?: boolean;
  /** Wraps the card, e.g. a router Link to the list behind the number */
  action?: (card: ReactNode) => ReactNode;
  className?: string;
}

/** One number on an overview page. Put 2–4 in a `grid gap-4 sm:grid-cols-2 lg:grid-cols-4`. */
export function StatCard({
  label,
  value,
  icon: Icon,
  hint,
  emphasis,
  loading,
  action,
  className,
}: StatCardProps) {
  const card = (
    <Card
      className={cn(
        'flex h-full items-start justify-between gap-4 p-5 transition-shadow',
        action && 'hover:shadow-md',
        emphasis && 'border-primary/40 bg-gradient-to-br from-primary/10 to-surface',
        className,
      )}
    >
      <div className="grid min-w-0 gap-1">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        {loading ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <p className="text-3xl font-bold tracking-tight tabular-nums">{value}</p>
        )}
        {hint && !loading && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      {Icon && (
        <span
          aria-hidden
          className={cn(
            'flex size-11 shrink-0 items-center justify-center rounded-xl',
            emphasis ? 'bg-primary text-primary-foreground' : 'bg-secondary/5 text-secondary',
          )}
        >
          <Icon className="size-5" />
        </span>
      )}
    </Card>
  );
  return action ? action(card) : card;
}
